"use client"

import { Cta, SanityCtaProps } from "@/components/cta"
import { GridBg } from "@/svgs/grid-bg"
import { Logo, LogoMobile } from "@/svgs/logo"
import { useLocale } from "@/modules/navigation"
import { cn } from "@/utils/cn"
import { ChevronDown } from "lucide-react"
import { Dropdown } from "@/ui/dropdown"
import { ComponentRef, CSSProperties, useEffect, useRef, useState } from "react"
import { RichText, RichTextValue } from "@/components/rich-text"
import { DrawingHighlight } from "@/contents/new-blocks/menu-hero/drawings-highlight"
import { AnimatePresence, motion, useScroll, useTransform } from "motion/react"
import { HamburgerIcon } from "@/svgs/hamburger-icon"
import { Split } from "./split"
import { JoinRebellion } from "@/svgs/join-rebellion"
import { ScrollAvatars } from "@/contents/new-blocks/menu-hero/scroll-avatars"
import { Cursor } from "@/components/cursor"

interface MenuHeroProps {
  content: {
    enableSplit: boolean
    showGrid: boolean
    bgColor: "blue" | "white"
    title: RichTextValue
    description?: string
    menu?: SanityCtaProps[]
    bottomCta: SanityCtaProps
    heroAvatar: "none" | "single-avatar" | "scroll-avatars"
    headerCtas?: {
      cta: SanityCtaProps
      logoCta: SanityCtaProps
      switch?: {
        isFirstSelected: boolean
        firstCta: SanityCtaProps
        secondCta: SanityCtaProps
      }
    }
  }
}

export function MenuHero({ content }: MenuHeroProps) {
  const containerRef = useRef<ComponentRef<"div">>(null)
  const [showCursor, setShowCursor] = useState(false)
  const [isMenuOpen, setIsMenuOpen] = useState(false)

  const { scrollYProgress } = useScroll({
    target: containerRef,
    offset: ["start start", "end end"],
    axis: "y",
  })
  /**
   * Need this because next run component on the server first, causing window to be undefined
   */
  const windowInnerWidth = typeof window !== "undefined" ? window.innerWidth : 0
  const [transformOutput, setTransformOutput] = useState([
    "0px",
    `${windowInnerWidth}px`,
  ])
  const splitX = useTransform(scrollYProgress, [0, 1], transformOutput)

  useEffect(() => {
    const handleResize = () =>
      setTransformOutput(["0px", `${window.innerWidth}px`])
    window.addEventListener("resize", handleResize)
    return () => window.removeEventListener("resize", handleResize)
  }, [])

  useEffect(() => {
    if (!isMenuOpen) {
      window.scrollTo({ top: 0 })
      return
    }
    document.body.style.overflow = "hidden"
    return () => {
      document.body.style.overflow = "auto"
    }
  }, [isMenuOpen])

  const splitDisabled = !content.enableSplit || isMenuOpen

  return (
    <motion.div
      className={cn(
        content.enableSplit ? "min-h-[150vh] md:min-h-[200vh]" : "h-auto"
      )}
      ref={containerRef}
      suppressHydrationWarning
      style={
        {
          "--split-x": splitX,
        } as CSSProperties
      }
    >
      <div
        className={cn(
          "overflow-hidden",
          content.enableSplit
            ? "sticky top-0 left-0 flex min-h-screen w-full flex-col"
            : "h-auto"
        )}
      >
        {content.enableSplit && (
          <Cursor
            color={content.bgColor === "white" ? "green" : "viola"}
            label="SCROLL"
            show={showCursor}
          />
        )}
        <AnimatePresence>
          {isMenuOpen && (
            <motion.div
              initial={{ height: 0 }}
              animate={{ height: "100vh" }}
              exit={{ height: 0 }}
              transition={{
                duration: 0.4,
              }}
              className="absolute top-0 left-0 z-20 w-full overflow-hidden bg-neutral-light"
            >
              <div className="pt-25 md:pt-[15vh]">
                <Menu
                  switch={content.headerCtas?.switch!}
                  cta={content.headerCtas?.cta!}
                  onClose={() => setIsMenuOpen(false)}
                  items={content.menu || []}
                />
              </div>
            </motion.div>
          )}
        </AnimatePresence>
        {/**
         * Background + Split bg
         * If bgColor is blue, then the mask is white
         * If bgColor is white, then the mask is blue
         */}
        <Split
          disabled={splitDisabled}
          globalClassName="absolute inset-0 w-full h-full -z-10"
          className={cn(
            "group absolute inset-0 h-full w-full",
            content.bgColor === "white"
              ? "bg-neutral-light data-[inverted=true]:bg-primary-800"
              : "bg-primary-800 data-[inverted=true]:bg-neutral-light"
          )}
        >
          {content.showGrid && (
            <GridBg
              className={cn(
                "absolute top-0 left-0 z-10 h-full w-full",
                content.bgColor === "white"
                  ? "text-viola-500 group-data-[inverted=true]:text-transparent"
                  : "text-transparent group-data-[inverted=true]:text-neutral-dark"
              )}
            />
          )}
        </Split>
        <motion.header
          className="relative z-30 mx-auto flex w-full max-w-xl items-center justify-between px-4 py-5 md:px-16 lg:px-20 lg:py-10 xl:px-35"
          initial={{ y: -20, opacity: 0 }}
          animate={{ y: 0, opacity: 1 }}
          transition={{
            duration: 0.6,
            ease: [0.25, 0.46, 0.45, 0.94],
          }}
        >
          <motion.div
            initial={{ y: -20, opacity: 0 }}
            animate={{ y: 0, opacity: 1 }}
            transition={{
              duration: 0.6,
              ease: [0.25, 0.46, 0.45, 0.94],
              delay: 0.1,
            }}
          >
            <Cta variant="blank" sanity={content.headerCtas?.logoCta}>
              <Split
                disabled={splitDisabled}
                className={cn(
                  content.bgColor === "white" || isMenuOpen
                    ? "text-neutral-dark data-[inverted=true]:text-neutral-light"
                    : "text-neutral-light data-[inverted=true]:text-neutral-dark"
                )}
              >
                <Logo className="hidden md:block" />
                <LogoMobile className="md:hidden" />
              </Split>
            </Cta>
          </motion.div>
          {content.headerCtas?.switch && (
            <motion.div
              initial={{ y: -20, opacity: 0 }}
              animate={{ y: 0, opacity: 1 }}
              transition={{
                duration: 0.3,
                delay: 0.2,
              }}
            >
              <Split
                disabled={splitDisabled}
                className={cn(
                  "group hidden items-center gap-4 rounded-3xl border-2 p-2 font-family-gthaptik md:flex",
                  content.bgColor === "white" || isMenuOpen
                    ? "border-neutral-dark data-[inverted=true]:border-neutral-light"
                    : "border-neutral-light data-[inverted=true]:border-neutral-dark"
                )}
              >
                <Cta
                  variant="ghost"
                  sanity={content.headerCtas.switch.firstCta}
                  className={cn(
                    "rounded-3xl p-3 font-family-gthaptik text-base font-light",
                    content.headerCtas.switch.isFirstSelected
                      ? "border border-primary-500 bg-green-500 text-primary-800 group-data-[inverted=true]:border-green-500"
                      : content.bgColor === "white" || isMenuOpen
                        ? "text-neutral-dark group-data-[inverted=true]:text-neutral-light"
                        : "text-neutral-light group-data-[inverted=true]:text-neutral-dark"
                  )}
                />
                <Cta
                  variant="ghost"
                  sanity={content.headerCtas.switch.secondCta}
                  className={cn(
                    "rounded-3xl p-3 font-family-gthaptik text-base font-light",
                    !content.headerCtas.switch.isFirstSelected
                      ? "border border-primary-500 bg-green-500 text-primary-800 group-data-[inverted=true]:border-green-500"
                      : content.bgColor === "white" || isMenuOpen
                        ? "text-neutral-dark group-data-[inverted=true]:text-neutral-light"
                        : "text-neutral-light group-data-[inverted=true]:text-neutral-dark"
                  )}
                />
              </Split>
            </motion.div>
          )}
          <motion.div
            className="flex items-center gap-4"
            initial={{ y: -20, opacity: 0 }}
            animate={{ y: 0, opacity: 1 }}
            transition={{
              duration: 0.3,
              delay: 0.3,
            }}
          >
            {content.headerCtas?.cta.label && (
              <Cta
                className="hidden lg:block"
                sanity={content.headerCtas.cta}
              />
            )}
            <LanguagePicker
              className={
                content.bgColor === "white" || splitDisabled
                  ? "text-neutral-dark data-[inverted=true]:text-neutral-light"
                  : "text-neutral-light data-[inverted=true]:text-neutral-dark"
              }
              splitDisabled={splitDisabled}
            />
            <Split
              disabled={splitDisabled}
              className={cn(
                content.bgColor === "white" || isMenuOpen
                  ? "text-neutral-dark data-[inverted=true]:text-neutral-light"
                  : "text-neutral-light data-[inverted=true]:text-neutral-dark"
              )}
            >
              <HamburgerIcon
                open={isMenuOpen}
                onClick={() => setIsMenuOpen(!isMenuOpen)}
              />
            </Split>
          </motion.div>
        </motion.header>
        {content.title && (
          <div
            onMouseEnter={() => setShowCursor(true)}
            onMouseLeave={() => setShowCursor(false)}
            className={cn(
              "mx-auto flex w-full max-w-[166rem] flex-1 flex-col items-center justify-center gap-y-6 overflow-hidden px-6 pb-10 text-center md:mt-15 md:items-start md:gap-y-0 md:px-16 md:text-left lg:mb-[10vh] lg:px-20 lg:pb-20 xl:px-35",
              content.enableSplit && "pb-30 md:pb-20"
            )}
          >
            <div className="overflow-hidden">
              <motion.div
                initial={{ y: "100%" }}
                animate={{ y: 0 }}
                transition={{
                  duration: 0.3,
                  delay: 0.9,
                }}
              >
                <Split
                  disabled={splitDisabled}
                  className={cn(
                    "group",
                    content.bgColor === "white"
                      ? "text-primary-800 data-[inverted=true]:text-neutral-light"
                      : "text-neutral-light data-[inverted=true]:text-primary-800"
                  )}
                >
                  <RichText
                    billboardClassName="text-viola-500"
                    value={content.title}
                    boxClassName="text-primary-500 group-data-[inverted=true]:text-neutral-light"
                  />
                </Split>
              </motion.div>
            </div>
            {content.description && (
              <div className="mt-12 max-w-[62rem] overflow-hidden font-family-gthaptik text-lg font-light md:mt-4 lg:mt-6">
                <motion.div
                  initial={{ y: "100%" }}
                  animate={{ y: 0 }}
                  transition={{
                    duration: 0.3,
                    delay: 1.5,
                  }}
                >
                  <Split
                    className={cn(
                      "font-family-gthaptik text-lg xl:text-xlg",
                      content.bgColor === "white"
                        ? "text-neutral-dark data-[inverted=true]:text-neutral-light"
                        : "text-neutral-light data-[inverted=true]:text-neutral-dark"
                    )}
                  >
                    {content.description}
                  </Split>
                </motion.div>
              </div>
            )}
            {content.bottomCta?.label && (
              <motion.div
                initial={{ y: "100%", opacity: 0 }}
                animate={{ y: 0, opacity: 1 }}
                transition={{
                  duration: 0.4,
                  delay: 1.8,
                }}
              >
                <Cta className="mt-12 md:mt-4" sanity={content.bottomCta} />
              </motion.div>
            )}
          </div>
        )}
        {/* Mobile JoinRebellion - bottom right */}
        {content.heroAvatar === "single-avatar" && (
          <div className="absolute right-0 bottom-0 translate-x-1/3 translate-y-1/4 md:hidden">
            <Split
              disabled={splitDisabled}
              className={cn(
                content.bgColor === "white"
                  ? "text-neutral-dark data-[inverted=true]:text-neutral-light"
                  : "text-neutral-light data-[inverted=true]:text-neutral-dark"
              )}
            >
              <JoinRebellion delay={1.5} className="h-[18rem] w-[18rem]" />
            </Split>
          </div>
        )}
        {/* Desktop JoinRebellion - bottom right */}
        {content.heroAvatar === "single-avatar" && (
          <div className="absolute right-8 bottom-8 hidden md:block">
            <Split
              disabled={splitDisabled}
              className={cn(
                content.bgColor === "white"
                  ? "text-neutral-dark data-[inverted=true]:text-neutral-light"
                  : "text-neutral-light data-[inverted=true]:text-neutral-dark"
              )}
            >
              <JoinRebellion
                delay={1.5}
                className="h-[38rem] w-[38rem] lg:h-[42rem] lg:w-[42rem] xxl:h-[58rem] xxl:w-[58rem]"
              />
            </Split>
          </div>
        )}
        {content.heroAvatar === "scroll-avatars" && (
          <ScrollAvatars scrollY={scrollYProgress} />
        )}
        {/**
         * Scroll indicator
         */}
        {!splitDisabled && (
          <motion.div
            initial={{ opacity: 0, y: 0 }}
            animate={{ opacity: 1, y: [-20, 0] }}
            transition={{
              type: "spring",
              opacity: { duration: 0.3, delay: 1.5 },
              y: {
                duration: 1,
                repeat: Infinity,
                repeatType: "reverse",
                ease: "easeInOut",
                delay: 2.1,
              },
            }}
            className="absolute bottom-8 left-1/2 -translate-x-1/2 flex-col"
          >
            <Split
              className={cn(
                "flex flex-col items-center gap-3",
                content.bgColor === "white"
                  ? "text-neutral-dark data-[inverted=true]:text-neutral-light"
                  : "text-neutral-light data-[inverted=true]:text-neutral-dark"
              )}
            >
              <div className="h-10 w-[2px] bg-current md:h-20" />
              <p className="text-center font-family-gtpressura text-base">
                Scroll for more
              </p>
            </Split>
          </motion.div>
        )}
      </div>
    </motion.div>
  )
}

const LANGUAGES = [
  { code: "en", name: "English" },
  { code: "it", name: "Italiano" },
]

function LanguagePicker({
  className,
  splitDisabled,
}: {
  className: string
  splitDisabled: boolean
}) {
  const { changeLocale, locale } = useLocale()

  return (
    <Dropdown
      trigger={
        <Split
          disabled={splitDisabled}
          className={cn(
            "flex items-center gap-2 font-family-gtpressura text-[1.8rem] uppercase group-data-[state=open]:scale-150",
            className
          )}
          aria-label="language picker"
        >
          {locale}
          <ChevronDown className="transition-transform duration-500" />
        </Split>
      }
      className="touch-manipulation space-y-2 rounded-sm border border-viola-500 bg-white p-2"
    >
      {LANGUAGES.map(({ code, name }) => (
        <div key={code}>
          <button
            type="button"
            onClick={() => changeLocale(code)}
            className={cn(
              locale === code && "font-bold",
              "flex min-h-11 items-center px-3 py-3 whitespace-nowrap"
            )}
          >
            <span>{name.toUpperCase()}</span>
          </button>
        </div>
      ))}
    </Dropdown>
  )
}

function Menu({
  items,
  onClose,
  switch: switchCtas,
  cta,
}: {
  onClose: () => void
  switch: NonNullable<
    NonNullable<MenuHeroProps["content"]["headerCtas"]>["switch"]
  >
  cta: NonNullable<MenuHeroProps["content"]["headerCtas"]>["cta"]
  items: SanityCtaProps[]
}) {
  const [hoveredItem, setHoveredItem] = useState<number | null>(null)

  const handleClose = (e: KeyboardEvent) => {
    if (e.key === "Escape") onClose()
  }

  useEffect(() => {
    window.addEventListener("keydown", handleClose)
    return () => window.removeEventListener("keydown", handleClose)
  }, [])

  return (
    <div className="mx-auto flex min-h-[calc(100vh-10rem)] max-w-xl flex-col items-center justify-center gap-8 px-6 md:items-stretch md:justify-start md:px-16 md:pt-[12.5vh] lg:px-20 xl:px-35">
      <motion.div
        className="my-auto md:hidden"
        initial={{ y: -20, opacity: 0 }}
        animate={{ y: 0, opacity: 1 }}
        transition={{
          duration: 0.3,
          delay: 0.2,
        }}
      >
        <div className="mx-auto flex max-w-max items-center gap-4 rounded-3xl border-2 border-neutral-dark p-2 font-family-gthaptik">
          <Cta
            variant="ghost"
            sanity={switchCtas.firstCta}
            className={cn(
              "rounded-3xl p-3 font-family-gthaptik text-base font-light",
              switchCtas.isFirstSelected
                ? "border border-primary-500 bg-green-500 text-primary-800"
                : "text-neutral-dark"
            )}
          />
          <Cta
            variant="ghost"
            sanity={switchCtas.secondCta}
            className={cn(
              "rounded-3xl p-3 font-family-gthaptik text-base font-light",
              !switchCtas.isFirstSelected
                ? "border border-primary-500 bg-green-500 text-primary-800"
                : "text-neutral-dark"
            )}
          />
        </div>
      </motion.div>
      {items.map((item, i) => (
        <DrawingHighlight
          key={i}
          startAnimation={hoveredItem === i}
          onMouseEnter={() => setHoveredItem(i)}
          onMouseLeave={() => setHoveredItem(null)}
          variant={(i % 5) + 1}
          className={cn(
            "relative w-max max-w-full",
            hoveredItem !== null && hoveredItem !== i && "-z-20"
          )}
        >
          <Cta
            className={cn(
              "font-family-archia text-2xl font-bold transition-colors duration-300 md:text-8xl xl:text-9xl",
              hoveredItem !== null && hoveredItem !== i
                ? "text-primary-50"
                : "text-neutral-dark"
            )}
            variant="ghost"
            sanity={item}
          />
        </DrawingHighlight>
      ))}
      <Cta className="mx-auto my-auto max-w-max md:hidden" sanity={cta} />
    </div>
  )
}
